import type { SttPort, SttRecordingResult } from "../../ports/stt.port";

// Capacitor STT-рекордер на нативном `capacitor-voice-recorder` (Android
// MediaRecorder → AAC). Доступ через `window.Capacitor.Plugins.VoiceRecorder`
// (нативный прокси, плагин авто-регистрируется `cap sync`) — БЕЗ npm-импорта,
// чтобы не тянуть пакет в web-бандл и не править vite external/optimizeDeps.
//
// Зачем нативно (а не getUserMedia): getUserMedia в Capacitor WebView нестабилен
// (Chromium NotReadableError, баг с 2018) — native MediaRecorder надёжнее.
// Возвращает AAC (`audio/aac`) — Gemini multimodal принимает напрямую, сервер
// валидирует в SUPPORTED_MIME_TYPES (rules/android-quirks.md).

interface VoiceRecorderPlugin {
	canDeviceVoiceRecord(): Promise<{ value: boolean }>;
	hasAudioRecordingPermission(): Promise<{ value: boolean }>;
	requestAudioRecordingPermission(): Promise<{ value: boolean }>;
	startRecording(): Promise<{ value: boolean }>;
	stopRecording(): Promise<{
		value: { recordDataBase64: string; msDuration: number; mimeType: string };
	}>;
	getCurrentStatus(): Promise<{ status: string }>;
}

function getRecorder(): VoiceRecorderPlugin | null {
	const cap = (
		window as unknown as {
			Capacitor?: { Plugins?: { VoiceRecorder?: VoiceRecorderPlugin } };
		}
	).Capacitor;
	return cap?.Plugins?.VoiceRecorder ?? null;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
	const bin = atob(base64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
	return new Blob([bytes], { type: mimeType });
}

// Локальный флаг записи (SttPort.isRecording() синхронный, а
// VoiceRecorder.getCurrentStatus() — async).
let recording = false;

export const sttCapacitorRecorderAdapter: SttPort = {
	isSupported() {
		return getRecorder() !== null;
	},
	async requestPermission() {
		const r = getRecorder();
		if (!r) return "denied";
		const has = await r.hasAudioRecordingPermission().catch(() => ({ value: false }));
		if (has.value) return "granted";
		const res = await r.requestAudioRecordingPermission().catch(() => ({ value: false }));
		return res.value ? "granted" : "denied";
	},
	async startRecording() {
		const r = getRecorder();
		if (!r) throw new Error("voice_recorder_unavailable");
		await r.startRecording();
		recording = true;
	},
	async stopRecording(): Promise<SttRecordingResult> {
		const r = getRecorder();
		recording = false;
		if (!r) throw new Error("voice_recorder_unavailable");
		const res = await r.stopRecording();
		const mimeType = res.value.mimeType || "audio/aac";
		return {
			blob: base64ToBlob(res.value.recordDataBase64, mimeType),
			mimeType,
			durationMs: res.value.msDuration,
		};
	},
	async cancelRecording() {
		// Плагин не имеет cancel — останавливаем запись и отбрасываем результат.
		if (!recording) return;
		recording = false;
		await getRecorder()
			?.stopRecording()
			.catch(() => {});
	},
	isRecording() {
		return recording;
	},
};
